import React, { useState, useEffect, useContext } from 'react';
import { Search, Edit, X, Plus, Trash2 } from 'lucide-react';
import AuthContext from '../../../context/AuthContext';
import { useToast } from '../../../context/ToastContext';
import userService from '../../../services/userService';
import { useNavigate } from 'react-router-dom';
import Pagination from '../../../components/Pagination';


export default function ManageUserRoles() {
    const {user,hasRole, authTokens } = useContext(AuthContext);

    const navigate = useNavigate();


    
    const { showError, showSuccess } = useToast();
    
    const [searchTerm, setSearchTerm] = useState('');
    const [showRoleModal, setShowRoleModal] = useState(false);
    const [selectedUser, setSelectedUser] = useState(null);
    const [selectedRoles, setSelectedRoles] = useState([]);
    const [users, setUsers] = useState([]);
    const [availableRoles, setAvailableRoles] = useState([]);
    const [loading, setLoading] = useState(false);
    
    const [pagination, setPagination] = useState({
        count: 0,
        page: 1,
        page_size: 10,
        total_pages: 0
    });

    // Load initial data
    useEffect(() => {
        loadUserRoles();
        loadAvailableRoles();
    }, [pagination.page, pagination.page_size]);

    const loadUserRoles = async () => {
        setLoading(true);
        try {
            const pagination_data = {
                page: pagination.page,
                page_size: pagination.page_size,
            };
            
            const result = await userService.getUserRoles(authTokens.access, pagination_data);
            if (result.success) {
                setUsers(result.data.results || result.data.users || []);
                
                // Update pagination info if available
                if (result.data.count !== undefined) {
                    setPagination(prev => ({
                        ...prev,
                        count: result.data.count,
                        total_pages: result.data.total_pages
                    }));
                }
            } else {
                console.error("Error:", result.error);
                if (result.status === 500) {
                    showError("Erreur du serveur, veuillez réessayer plus tard");
                } else {
                    showError(result.error);
                }
            }
        } catch (error) {
            console.error('Error loading user roles:', error);
            showError('Erreur lors du chargement des rôles utilisateur');
        } finally {
            setLoading(false);
        }
    };

    const loadAvailableRoles = async () => {
        try {
            const result = await userService.getAvailableRoles(authTokens.access);
            if (result.success) {
                setAvailableRoles(result.data.roles || []);
            } else {
                console.error("Error loading roles:", result.error);
                if (result.status === 500) {
                    showError("Erreur du serveur lors du chargement des rôles");
                } else {
                    showError(result.error);
                }
            }
        } catch (error) {
            console.error('Error loading available roles:', error);
            showError('Erreur lors du chargement des rôles disponibles');
        }
    };

    const handlePageChange = (newPage) => {
        setPagination(prev => ({ ...prev, page: newPage }));
    };

    const handlePageSizeChange = (newPageSize) => {
        setPagination(prev => ({ 
            ...prev, 
            page_size: newPageSize, 
            page: 1 // Reset to first page
        }));
    };

    const handleSearch = () => {
        console.log('Searching for:', searchTerm);
        setPagination(prev => ({ ...prev, page: 1 }));
        // Backend search functionality will be implemented later
        setTimeout(() => loadUserRoles(), 0);
    };

    const handleEditRoles = (user) => {
        setSelectedUser(user);
        // setSelectedRoles(user.roles.map(role => role.id));
        setSelectedRoles(
            user.roles
                .filter(role => role.is_active)  // ✅ only active roles
                .map(role => role.id)
            );
        setShowRoleModal(true);
    };

    const handleRoleToggle = (roleId) => {
        setSelectedRoles(prev => 
            prev.includes(roleId) 
                ? prev.filter(id => id !== roleId)
                : [...prev, roleId]
        );
    };

    const handleApplyRoleChanges = async () => {
        if (selectedUser) {
            try {
                setLoading(true);
                
                const result = await userService.assignRolesToUser(
                    selectedUser.id,
                    selectedRoles,
                    authTokens.access
                );
                
                if (result.success) {
                    await loadUserRoles(); // Reload data
                    setShowRoleModal(false);
                    setSelectedUser(null);
                    setSelectedRoles([]);
                    showSuccess(`Rôles mis à jour pour ${selectedUser.email}`);
                } else {
                    console.error('Error assigning roles:', result.error);
                    if (result.status === 500) {
                        showError("Erreur du serveur lors de l'assignation des rôles");
                    } else {
                        showError(result.error);
                    }
                }
                
            } catch (error) {
                console.error('Error updating roles:', error);
                showError('Erreur lors de la mise à jour des rôles');
            } finally {
                setLoading(false);
            }
        }
    };

    const handleRemoveRole = async (user, roleToRemove) => {
        try {
            setLoading(true);
            
            const result = await userService.removeRoleFromUser(
                user.id,
                roleToRemove.id,
                authTokens.access
            );
            
            if (result.success) {
                await loadUserRoles(); // Reload data
                showSuccess(`Rôle ${roleToRemove.name} supprimé de ${user.email}`);
            } else {
                console.error('Error removing role:', result.error);
                if (result.status === 500) {
                    showError("Erreur du serveur lors de la suppression du rôle");
                } else {
                    showError(result.error);
                }
            }
            
        } catch (error) {
            console.error('Error removing role:', error);
            showError('Erreur lors de la suppression du rôle');
        } finally {
            setLoading(false);
        }
    };

    const handleCancelRoleChange = () => {
        setShowRoleModal(false);
        setSelectedUser(null);
        setSelectedRoles([]);
    };

    const getRoleBadge = (role) => {
        const badgeClasses = {
            'Admin': 'bg-red-100 text-red-800',
            'RiskManager': 'bg-blue-100 text-blue-800',
            'RiskIdentifier': 'bg-yellow-100 text-yellow-800',
            'CartoViewer': 'bg-purple-100 text-purple-800',
            // Legacy support
            'Administrateur': 'bg-red-100 text-red-800',
            'Gestionnaire': 'bg-blue-100 text-blue-800',
            'RiskMananger': 'bg-blue-100 text-blue-800',
            'Utilisateur': 'bg-green-100 text-green-800'
        };
        
        return `px-2 py-1 text-xs font-medium rounded-full ${badgeClasses[role.name] || 'bg-gray-100 text-gray-800'}`;
    };

    const getStatusBadge = (status) => {
        const badgeClasses = {
            'Actif': 'bg-green-100 text-green-800',
            'Inactif': 'bg-red-100 text-red-800'
        };
        
        return `px-2 py-1 text-xs font-medium rounded-full ${badgeClasses[status] || 'bg-gray-100 text-gray-800'}`;
    };

    const filteredUsers = users.filter(user => 
        user.email.toLowerCase().includes(searchTerm.toLowerCase())
    );

    
    if (!hasRole("Admin")) {
        navigate(-1)
    }

    return (
        <div className="space-y-6">
            {/* Header with Search */}
            <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
                <div>
                    <h2 className="text-xl font-semibold text-gray-900">Gestion des Rôles Utilisateur</h2>
                    <p className="text-sm text-gray-600 mt-1">
                        {users.length} utilisateur{users.length > 1 ? 's' : ''} • {availableRoles.length} rôle{availableRoles.length > 1 ? 's' : ''} disponible{availableRoles.length > 1 ? 's' : ''}
                    </p>
                </div>
                
                <div className="flex items-center space-x-2 w-full sm:w-auto">
                    <div className="relative flex-1 sm:flex-initial">
                        <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
                        <input
                            type="text"
                            value={searchTerm}
                            onChange={(e) => setSearchTerm(e.target.value)}
                            placeholder="Rechercher par email..."
                            className="w-full sm:w-80 pl-10 pr-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-teal-500 focus:border-transparent"
                            onKeyPress={(e) => e.key === 'Enter' && handleSearch()}
                        />
                    </div>
                    <button
                        onClick={handleSearch}
                        className="flex items-center px-4 py-2 bg-teal-600 text-white rounded-lg hover:bg-teal-700 hover:shadow-md transition-all duration-200"
                        disabled={loading}
                    >
                        <Search className="h-4 w-4 mr-2" />
                        Rechercher
                    </button>
                </div>
            </div>

            {/* Table Section */}
            <div className="bg-white border border-gray-200 rounded-lg shadow-sm overflow-hidden">
                <div className="overflow-x-auto">
                    <table className="min-w-full divide-y divide-gray-200">
                        <thead className="bg-gray-50">
                            <tr>
                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                    Utilisateur
                                </th>
                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                    Type d'utilisateur
                                </th>
                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                    Statut
                                </th>
                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                    Actions
                                </th>
                            </tr>
                        </thead>
                        <tbody className="bg-white divide-y divide-gray-200">
                            {filteredUsers.map((user) => (
                                <tr key={user.id} className="hover:bg-gray-50">
                                    <td className="px-6 py-4 whitespace-nowrap">
                                        <div className="text-sm font-medium text-gray-900">
                                            {user.email}
                                        </div>
                                        <div className="text-xs text-gray-500">
                                            {user.first_name} {user.last_name}
                                        </div>
                                    </td>
                                    <td className="px-6 py-4">
                                        <div className="flex flex-wrap gap-1">
                                            {user.roles.map((role) => (
                                                role.is_active && (
                                                    <div key={role.id} className="flex items-center gap-1">
                                                        <span className={getRoleBadge(role)}>
                                                            {role.name}
                                                        </span>
                                                        <button
                                                            onClick={() => handleRemoveRole(user, role)}
                                                            className="text-red-500 hover:text-red-700 ml-1"
                                                            title={`Supprimer le rôle ${role.name}`}
                                                            disabled={loading}
                                                        >
                                                            <X className="h-3 w-3" />
                                                        </button>
                                                    </div>
                                                )
                                            ))}
                                            {user.roles.filter(role => role.is_active).length === 0 && (
                                                <span className="text-sm text-gray-500 italic">
                                                    Aucun rôle actif
                                                </span>
                                            )}
                                        </div>
                                    </td>
                                    <td className="px-6 py-4 whitespace-nowrap">
                                        <span className={getStatusBadge(user.status)}>
                                            {user.status}
                                        </span>
                                    </td>
                                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                                        <button 
                                            onClick={() => handleEditRoles(user)}
                                            className="flex items-center px-3 py-1 bg-blue-500 text-white text-xs rounded-md hover:bg-blue-600 transition-colors duration-200"
                                            disabled={loading}
                                        >
                                            <Edit className="h-3 w-3 mr-1" />
                                            Modifier rôles
                                        </button>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>

                <Pagination
                    currentPage={pagination.page}
                    totalPages={pagination.total_pages}
                    totalItems={pagination.count}
                    pageSize={pagination.page_size}
                    onPageChange={handlePageChange}
                    onPageSizeChange={handlePageSizeChange}
                    loading={loading}
                    itemName="Utilisateurs" 
                />

                {/* Empty State */}
                {filteredUsers.length === 0 && !loading && (
                    <div className="text-center py-12">
                        <div className="text-gray-500">
                            <p className="text-lg font-medium">Aucun utilisateur trouvé</p>
                            <p className="mt-1">Essayez de modifier votre recherche.</p>
                        </div>
                    </div>
                )}
            </div>

           {/* Role Edit Modal */}
            {showRoleModal && (
            <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
                <div className="bg-white rounded-lg shadow-xl max-w-md w-full max-h-[90vh] overflow-y-auto">
                
                {/* Modal Header */}
                <div className="flex items-center justify-between p-6 border-b border-gray-200">
                    <h3 className="text-lg font-medium text-gray-900">
                    Modifier les rôles
                    </h3>
                    <button
                    onClick={handleCancelRoleChange}
                    className="text-gray-400 hover:text-gray-600 transition-colors"
                    disabled={loading}
                    >
                    <X className="h-5 w-5" />
                    </button>
                </div>

                {/* Modal Content */}
                <div className="p-6">
                    {/* User info */}
                    <div className="mb-6">
                    <p className="text-sm text-gray-600 mb-2">
                        Utilisateur:{" "}
                        <span className="font-medium text-gray-900">
                        {selectedUser?.email}
                        </span>
                    </p>

                    {/* Active roles */}
                    <div className="text-sm text-gray-600">
                        Rôles actuels:
                        <div className="flex flex-wrap gap-1 mt-1">
                        {selectedUser?.roles?.some((r) => r.is_active) ? (
                            selectedUser.roles
                            .filter((role) => role.is_active)
                            .map((role) => (
                                <span key={role.id} className={getRoleBadge(role)}>
                                {role.name}
                                </span>
                            ))
                        ) : (
                            <span className="text-gray-500 italic">Aucun rôle actif</span>
                        )}
                        </div>
                    </div>
                    </div>

                    {/* Available roles */}
                    <div className="space-y-3">
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                        Sélectionner les rôles :
                    </label>

                    {availableRoles.length > 0 ? (
                        availableRoles.map((role) => (
                        <label
                            key={role.id}
                            className="flex items-start p-3 border border-gray-200 rounded-lg hover:bg-gray-50 cursor-pointer"
                        >
                        <input
                            type="checkbox"
                            checked={selectedRoles.includes(role.id)}
                            onChange={() => handleRoleToggle(role.id)}
                            className="h-4 w-4 text-teal-600 border-gray-300 rounded focus:ring-teal-500 mt-0.5"
                            disabled={loading}
                        />

                            <div className="ml-3 flex-1">
                            <div className="flex items-center gap-2">
                                <span className="text-sm font-medium text-gray-700">
                                    {role.name}
                                </span>
                                {selectedUser?.roles.some(userRole => userRole.id === role.id && userRole.is_active) && (
                                    <span className="text-xs bg-green-100 text-green-800 px-2 py-0.5 rounded-full">
                                        Actuel
                                    </span>
                                )}
                            </div>
                            {role.description && (
                                <p className="text-xs text-gray-500 mt-1">
                                {role.description}
                                </p>
                            )}
                            </div>
                        </label>
                        ))
                    ) : (
                        <p className="text-sm text-gray-500 italic">Aucun rôle disponible</p>
                    )}
                    </div>

                    {/* Selected roles preview */}
                    {selectedRoles.length > 0 && (
                    <div className="mt-4 p-3 bg-gray-50 rounded-lg">
                        <p className="text-sm text-gray-600 mb-2">Rôles sélectionnés :</p>
                        <div className="flex flex-wrap gap-1">
                        {availableRoles
                            .filter((role) => selectedRoles.includes(role.id))
                            .map((role) => (
                            <span key={role.id} className={getRoleBadge(role)}>
                                {role.name}
                            </span>
                            ))}
                        </div>
                    </div>
                    )}
                </div>

                {/* Modal Footer */}
                <div className="flex items-center justify-end space-x-3 p-6 border-t border-gray-200">
                    <button
                    onClick={handleCancelRoleChange}
                    className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 rounded-md hover:bg-gray-200 transition-colors duration-200"
                    disabled={loading}
                    >
                    Annuler
                    </button>

                    <button
                    onClick={handleApplyRoleChanges}
                    disabled={loading}
                    className="px-4 py-2 text-sm font-medium text-white bg-teal-600 rounded-md hover:bg-teal-700 disabled:bg-gray-300 disabled:cursor-not-allowed transition-colors duration-200 flex items-center"
                    >
                    {loading && (
                        <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                    )}
                    Appliquer
                    </button>
                </div>
                </div>
            </div>
            )}


            {/* Loading Overlay */}
            {loading && (
                <div className="fixed inset-0 bg-black bg-opacity-25 flex items-center justify-center z-40">
                    <div className="bg-white rounded-lg p-6 flex items-center space-x-3">
                        <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-teal-600"></div>
                        <span className="text-gray-700">Chargement...</span>
                    </div>
                </div>
            )}
        </div>
    );
}